<script lang="ts" setup>
  import { Navigation } from 'swiper/modules'
  import arrowIcon from '@/assets/icons/components/swiper-nav-arrow.svg?url'
  import type { CCardItemData } from '@/components/common/CCardItem/CCardItem.types'
  import { mdAndUp } from '@/composables/breakpoints'
  import type { ImageProviderSource, ImgLoading } from '@/types/media'

  const props = withDefaults(
    defineProps<{
      id: string
      alignment?: 'left' | 'centre' | 'right'
      backgroundColor?: string
      darkMode?: boolean
      items: CCardItemData[]
      loading: ImgLoading
      preload?: boolean
      provider?: ImageProviderSource
    }>(),
    {
      alignment: 'left',
      backgroundColor: 'inherit',
      darkMode: false,
      loading: 'lazy',
      preload: false,
      provider: 'contentful'
    }
  )

  const slideLength = computed<number>(() => props.items.length)
</script>

<template>
  <div class="c-card-carousel">
    <client-only>
      <div :class="['swiper-button-prev', `swiper-button-prev-${id}`, { 'swiper-button-hidden': !mdAndUp }]">
        <img :src="arrowIcon" alt="previous" class="swiper-nav-icon" />
      </div>

      <div :class="['swiper-button-next', `swiper-button-next-${id}`, { 'swiper-button-hidden': !mdAndUp }]">
        <img :src="arrowIcon" alt="next" class="swiper-nav-icon swiper-nav-icon--flipped" />
      </div>

      <swiper-container
        :breakpoints="{
          0: {
            slidesPerView: 1.6,
            slidesOffsetBefore: 16,
            slidesOffsetAfter: 16,
            spaceBetween: 16,
            navigation: {
              enabled: false
            }
          },
          576: {
            slidesPerView: 2.2,
            slidesOffsetBefore: 24,
            slidesOffsetAfter: 24,
            spaceBetween: 16,
            navigation: {
              enabled: false
            }
          },
          768: {
            slidesPerView: 3,
            slidesPerGroup: slideLength > 3 ? 2 : 1,
            spaceBetween: 32,
            slidesOffsetBefore: 24,
            slidesOffsetAfter: 24,
            navigation: {
              enabled: true
            }
          },
          992: {
            slidesPerView: slideLength > 3 ? 4 : 3,
            slidesPerGroup: slideLength > 3 ? 2 : 1,
            spaceBetween: 32,
            slidesOffsetBefore: 24,
            slidesOffsetAfter: 24,
            navigation: {
              enabled: true
            }
          },
          1200: {
            slidesPerView: slideLength > 3 ? 4 : 3,
            slidesPerGroup: slideLength > 3 ? 2 : 1,
            spaceBetween: 32,
            slidesOffsetBefore: 24,
            slidesOffsetAfter: 24,
            navigation: {
              enabled: true
            }
          },
          1400: {
            slidesPerView: slideLength > 3 ? 4 : 3,
            slidesPerGroup: slideLength > 3 ? 2 : 1,
            spaceBetween: 32,
            slidesOffsetBefore: 24,
            slidesOffsetAfter: 24,
            navigation: {
              enabled: true
            }
          }
        }"
        :modules="[Navigation]"
        :navigation="{
          nextEl: `.swiper-button-next-${id}`,
          prevEl: `.swiper-button-prev-${id}`,
          addIcons: false
        }"
        class="card-carousel"
      >
        <swiper-slide v-for="item in items" :key="item.id" class="card-carousel-slide">
          <CCardItem
            :alignment
            :aspect-ratio="{
              desktop: { denominator: 4, numerator: 5 },
              mobile: { denominator: 4, numerator: 5 }
            }"
            :dark-mode="darkMode"
            :data="item"
            :loading
            :preload
            :provider
            class="slide-card-item"
          />
        </swiper-slide>
      </swiper-container>
    </client-only>
  </div>
</template>

<style lang="scss" scoped>
  .c-card-carousel {
    position: relative;
    margin: 0 auto;
    background-color: v-bind(backgroundColor);

    .card-carousel-slide {
      display: flex;
      height: auto;

      .slide-card-item {
        width: 100%;
        height: 100%;

        padding-bottom: 1px;
      }
    }

    .swiper-nav-icon {
      width: 64px;
      height: 64px;
      display: block;
      object-fit: contain;
      pointer-events: none;
    }

    .swiper-nav-icon--flipped {
      transform: rotate(180deg);
    }
  }

  @include screenMdAndUp {
    .c-card-carousel:has(
        .swiper-button-prev:not(.swiper-button-disabled):not(.swiper-button-lock):not(.swiper-button-hidden)
      )
      .card-carousel {
      -webkit-mask-image: linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 6%, rgba(0, 0, 0, 1) 100%);
      mask-image: linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 6%, rgba(0, 0, 0, 1) 100%);
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: left center;
      mask-position: left center;
    }

    .c-card-carousel:has(
        .swiper-button-next:not(.swiper-button-disabled):not(.swiper-button-lock):not(.swiper-button-hidden)
      )
      .card-carousel {
      -webkit-mask-image: linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 6%, rgba(0, 0, 0, 1) 100%);
      mask-image: linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 6%, rgba(0, 0, 0, 1) 100%);
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: right center;
      mask-position: right center;
    }

    .c-card-carousel:has(
        .swiper-button-prev:not(.swiper-button-disabled):not(.swiper-button-lock):not(.swiper-button-hidden)
      ):has(.swiper-button-next:not(.swiper-button-disabled):not(.swiper-button-lock):not(.swiper-button-hidden))
      .card-carousel {
      -webkit-mask-image: linear-gradient(
        to right,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 6%,
        rgba(0, 0, 0, 1) 94%,
        rgba(0, 0, 0, 0) 100%
      );
      mask-image: linear-gradient(
        to right,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 1) 6%,
        rgba(0, 0, 0, 1) 94%,
        rgba(0, 0, 0, 0) 100%
      );
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      -webkit-mask-position: center;
      mask-position: center;
    }
  }
</style>
